import { useEffect, useRef, useCallback } from "react";
import { Card, CardBody, Spinner, Chip, Avatar, Button, Input } from "@heroui/react";
import {
  Trophy,
  RefreshCw,
  ExternalLink,
  Target,
  TrendingUp,
  TrendingDown,
  Award,
  Activity,
  Search,
} from "lucide-react";
import { useLeaderboardStore } from "@/stores/leaderboardStore";
import { formatPercentage } from "@/lib/utils";
import type { UserTrackRecord, PerformanceTier } from "@/types";

function getRankBadge(rank: number) {
  if (rank === 1)
    return (
      <div className="flex items-center gap-1">
        <Trophy className="w-5 h-5 text-yellow-500" />
        <span className="font-bold text-yellow-500">1</span>
      </div>
    );
  if (rank === 2)
    return (
      <div className="flex items-center gap-1">
        <Trophy className="w-5 h-5 text-gray-400" />
        <span className="font-bold text-gray-400">2</span>
      </div>
    );
  if (rank === 3)
    return (
      <div className="flex items-center gap-1">
        <Trophy className="w-5 h-5 text-amber-600" />
        <span className="font-bold text-amber-600">3</span>
      </div>
    );
  return <span className="text-default-500">{rank}</span>;
}

function TierBadge({ tier }: { tier: PerformanceTier }) {
  const config = {
    top_performer: { color: "success" as const, label: "Top Performer", icon: Award },
    proven: { color: "primary" as const, label: "Proven", icon: Target },
    unproven: { color: "default" as const, label: "Unproven", icon: Activity },
  };
  const { color, label, icon: Icon } = config[tier];

  return (
    <Chip size="sm" color={color} variant="flat" startContent={<Icon className="w-3 h-3" />}>
      {label}
    </Chip>
  );
}

function AccuracyCell({ value, label }: { value: number | null; label: string }) {
  if (value === null) return <span className="text-default-400">-</span>;

  const color =
    value >= 65 ? "text-success" : value >= 55 ? "text-primary" : value >= 50 ? "text-warning" : "text-danger";

  return (
    <div className="flex flex-col">
      <span className={`font-semibold ${color}`}>{value.toFixed(1)}%</span>
      <span className="text-xs text-default-400">{label}</span>
    </div>
  );
}

function ReturnCell({ value }: { value: number | null }) {
  if (value === null) return <span className="text-default-400">-</span>;

  const isPositive = value >= 0;
  const Icon = isPositive ? TrendingUp : TrendingDown;
  const color = isPositive ? "text-success" : "text-danger";

  return (
    <div className={`flex items-center gap-1 ${color}`}>
      <Icon className="w-3 h-3" />
      <span>{formatPercentage(value)}</span>
    </div>
  );
}

function LeaderboardRow({ record, rank }: { record: UserTrackRecord; rank: number }) {
  // Build Twitter URL - use different formats for numeric IDs vs usernames
  const isNumericHandle = /^\d+$/.test(record.twitterHandle);
  const twitterUrl = isNumericHandle
    ? `https://x.com/i/user/${record.twitterHandle}` // Numeric ID format
    : `https://x.com/${record.twitterHandle}`; // Username format

  return (
    <tr className="border-b border-divider hover:bg-default-50 transition-colors">
      <td className="py-4 px-4">{getRankBadge(rank)}</td>
      <td className="py-4 px-4">
        <div className="flex items-center gap-3">
          <Avatar
            name={record.twitterHandle.charAt(0).toUpperCase()}
            size="sm"
            className="bg-primary/10 text-primary"
          />
          <div>
            <a
              href={twitterUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium hover:text-primary flex items-center gap-1"
            >
              {isNumericHandle ? `@${record.twitterHandle.slice(0, 12)}...` : `@${record.twitterHandle}`}
              <ExternalLink className="w-3 h-3" />
            </a>
            <div className="text-xs text-default-400">
              {record.bullishSignals} bullish / {record.bearishSignals} bearish
            </div>
          </div>
        </div>
      </td>
      <td className="py-4 px-4">
        <TierBadge tier={record.performanceTier} />
      </td>
      <td className="py-4 px-4">
        <AccuracyCell value={record.accuracy24h} label="24h" />
      </td>
      <td className="py-4 px-4">
        <AccuracyCell value={record.accuracy7d} label="7d" />
      </td>
      <td className="py-4 px-4">
        <AccuracyCell value={record.accuracy30d} label="30d" />
      </td>
      <td className="py-4 px-4">
        <ReturnCell value={record.avgReturn24h} />
      </td>
      <td className="py-4 px-4">
        <div className="flex flex-col">
          <span className="font-medium">{record.signalsWith24hOutcome}</span>
          <span className="text-xs text-default-400">verified</span>
        </div>
      </td>
      <td className="py-4 px-4">
        <span className="text-default-500">{record.uniqueTokens}</span>
      </td>
    </tr>
  );
}

export function Leaderboard() {
  const {
    leaderboardLoading,
    leaderboardError,
    hasMore,
    loadLeaderboard,
    loadMoreLeaderboard,
    minSignals,
    setMinSignals,
    searchQuery,
    setSearchQuery,
    getFilteredRecords,
  } = useLeaderboardStore();

  const observerTarget = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadLeaderboard();
  }, []);

  // Infinite scroll observer
  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [target] = entries;
      if (target.isIntersecting && hasMore && !leaderboardLoading) {
        loadMoreLeaderboard();
      }
    },
    [hasMore, leaderboardLoading, loadMoreLeaderboard]
  );

  useEffect(() => {
    const element = observerTarget.current;
    if (!element) return;

    const observer = new IntersectionObserver(handleObserver, {
      threshold: 0.5,
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, [handleObserver]);

  const handleRefresh = () => {
    loadLeaderboard();
  };

  const handleMinSignalsChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = parseInt(e.target.value);
    setMinSignals(value);
    loadLeaderboard(value);
  };

  // Get filtered records from store
  const trackRecords = getFilteredRecords();

  // Stats summary
  const topPerformers = trackRecords.filter((r) => r.performanceTier === "top_performer").length;
  const proven = trackRecords.filter((r) => r.performanceTier === "proven").length;
  const avgAccuracy =
    trackRecords.length > 0
      ? trackRecords.reduce((sum, r) => sum + (r.accuracy24h ?? 0), 0) / trackRecords.length
      : 0;

  return (
    <Card className="w-full">
      <CardBody className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Trophy className="w-6 h-6 text-primary" />
            <div>
              <h2 className="text-xl font-bold">Prediction Accuracy Leaderboard</h2>
              <p className="text-sm text-default-500">
                Ranked by accuracy of bullish/bearish calls predicting price direction
              </p>
            </div>
          </div>
          <Button
            size="sm"
            variant="flat"
            startContent={<RefreshCw className="w-4 h-4" />}
            onPress={handleRefresh}
            isLoading={leaderboardLoading}
          >
            Refresh
          </Button>
        </div>

        {/* Stats summary */}
        {trackRecords.length > 0 && (
          <div className="grid grid-cols-3 gap-4 mb-6 p-4 bg-default-50 rounded-lg">
            <div className="text-center">
              <div className="text-2xl font-bold text-success">{topPerformers}</div>
              <div className="text-xs text-default-500">Top Performers (65%+)</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-primary">{proven}</div>
              <div className="text-xs text-default-500">Proven (55%+)</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold">{avgAccuracy.toFixed(1)}%</div>
              <div className="text-xs text-default-500">Avg 24h Accuracy</div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="space-y-4 mb-4">
          {/* Search */}
          <div>
            <Input
              placeholder="Search by Twitter handle..."
              value={searchQuery}
              onValueChange={setSearchQuery}
              startContent={<Search className="w-4 h-4 text-default-400" />}
              classNames={{
                inputWrapper: "bg-default-50",
              }}
            />
          </div>

          {/* Min Signals Filter */}
          <div className="p-4 bg-default-50 rounded-lg">
            <label className="text-sm font-medium mb-2 block">Min Token Recommendations</label>
            <select
              value={minSignals}
              onChange={handleMinSignalsChange}
              className="w-full max-w-xs px-3 py-2 rounded-lg border border-default-200 bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {[3, 5, 10, 15, 20, 25, 30, 50].map((val) => (
                <option key={val} value={val}>
                  {val}+ signals
                </option>
              ))}
            </select>
            <p className="text-xs text-default-400 mt-2">
              Filter analysts by minimum number of unique token recommendations
            </p>
          </div>
        </div>

        {leaderboardError && (
          <div className="p-4 mb-4 bg-danger-50 text-danger rounded-lg">{leaderboardError}</div>
        )}

        {leaderboardLoading && trackRecords.length === 0 ? (
          <div className="flex flex-col justify-center items-center py-12 gap-4">
            <Spinner size="lg" />
            <p className="text-sm text-default-400">
              Analyzing signals and calculating track records...
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-divider text-left text-xs text-default-500 uppercase">
                    <th className="py-3 px-4 font-medium">Rank</th>
                    <th className="py-3 px-4 font-medium">Analyst</th>
                    <th className="py-3 px-4 font-medium">Tier</th>
                    <th className="py-3 px-4 font-medium">24h Acc</th>
                    <th className="py-3 px-4 font-medium">7d Acc</th>
                    <th className="py-3 px-4 font-medium">30d Acc</th>
                    <th className="py-3 px-4 font-medium">Avg Return</th>
                    <th className="py-3 px-4 font-medium">Signals</th>
                    <th className="py-3 px-4 font-medium">Tokens</th>
                  </tr>
                </thead>
                <tbody>
                  {trackRecords.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="py-8 text-center text-default-400">
                        {searchQuery
                          ? "No analysts found matching your search"
                          : "No analysts found with verified track records"}
                      </td>
                    </tr>
                  ) : (
                    trackRecords.map((record, index) => (
                      <LeaderboardRow
                        key={record.twitterHandle}
                        record={record}
                        rank={index + 1}
                      />
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Infinite scroll trigger */}
            {hasMore && trackRecords.length > 0 && !searchQuery && (
              <div ref={observerTarget} className="flex justify-center py-8">
                {leaderboardLoading ? (
                  <Spinner size="md" />
                ) : (
                  <p className="text-sm text-default-400">Scroll to load more...</p>
                )}
              </div>
            )}

            {/* Results count */}
            {trackRecords.length > 0 && (
              <div className="mt-4 text-sm text-default-400 text-center">
                Showing {trackRecords.length} {searchQuery ? "matching" : ""} analysts
                {hasMore && !searchQuery && " (scroll for more)"}
              </div>
            )}

            {/* Legend */}
            <div className="mt-4 p-4 bg-default-50 rounded-lg text-xs text-default-500">
              <strong>How accuracy is calculated:</strong> Each signal (bullish/bearish tweet) is
              compared against actual price movement. A bullish call is correct if price increased;
              bearish is correct if price decreased. Top Performers: 65%+, Proven: 55%+, Unproven:
              &lt;55% or fewer than 10 signals.
            </div>
          </>
        )}
      </CardBody>
    </Card>
  );
}
